// AccountNeverTranslateModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Tag, Tooltip, message, Spin, Space } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, QuestionCircleOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';
import { isValidGlossaryTerm } from '../utils/glossaryValidation';
import './AccountTranslationRulesModal.css';

interface AccountNeverTranslateModalProps {
  isVisible: boolean;
  onClose: () => void;
  currentLanguage: string;
  neverTranslateWords: { [key: string]: string[] };
  setNeverTranslateWords: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  userID: string;
}

const AccountNeverTranslateModal: React.FC<AccountNeverTranslateModalProps> = ({
  isVisible,
  onClose,
  currentLanguage,
  neverTranslateWords,
  setNeverTranslateWords,
  userID,
}) => {
  const [newWord, setNewWord] = useState('');
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [editedWord, setEditedWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setNewWord('');
      setEditingWord(null);
      setEditedWord('');
    }
  }, [isVisible]);

  const toStorageKey = (lang: string): string => lang.replace(/-/g, '_').replace(/ /g, '_');
  const langKey = toStorageKey(currentLanguage);

  const updateAPI = async (words: string[]): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await axios.post('https://askpoeai-nswdzfky7q-uc.a.run.app/update-account-never-translate', {
        userID: userID,
        language: currentLanguage,
        words: words,
      });
      if (response.status !== 200) {
        throw new Error('Failed to update');
      }
      return true;
    } catch (error) {
      console.error('Error updating:', error);
      message.error('Failed to update. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isValidInput = isValidGlossaryTerm;

  const normalizeTerm = (input: string) => input.trim().toLocaleLowerCase();
  const splitTerms = (value: string) => value.split('|').map(entry => entry.trim()).filter(Boolean);

  const handleAddWord = async () => {
    const entries = splitTerms(newWord);
    if (!entries.length) {
      message.error('Enter at least one word or phrase to add.');
      return;
    }

    const existingWords = neverTranslateWords[langKey] || [];
    const existingKeys = new Set(existingWords.map(word => normalizeTerm(word)));
    const validEntries: string[] = [];
    const invalidEntries: string[] = [];
    const duplicateEntries: string[] = [];

    entries.forEach(entry => {
      if (!isValidInput(entry)) {
        invalidEntries.push(entry);
        return;
      }
      const key = normalizeTerm(entry);
      if (existingKeys.has(key)) {
        duplicateEntries.push(entry);
        return;
      }
      existingKeys.add(key);
      validEntries.push(entry);
    });

    if (!validEntries.length) {
      message.warning('No new valid text to add.');
      return;
    }

    const updatedWords = [...existingWords, ...validEntries];
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords(prev => ({
        ...prev,
        [langKey]: updatedWords
      }));
      setNewWord('');
      message.success(`Added ${validEntries.length} new entr${validEntries.length > 1 ? 'ies' : 'y'}.`);
      if (invalidEntries.length) {
        message.warning(`Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${invalidEntries.join('; ')}`);
      }
      if (duplicateEntries.length) {
        message.info(`Ignored ${duplicateEntries.length} duplicate entr${duplicateEntries.length > 1 ? 'ies' : 'y'}: ${duplicateEntries.join('; ')}`);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingWord) {
      message.error('Please select a word to edit.');
      return;
    }

    const trimmed = editedWord.trim();
    if (!trimmed) {
      message.error('Please enter a valid word or phrase.');
      return;
    }
    if (!isValidInput(trimmed)) {
      message.error('Please enter a valid word or phrase (letters, numbers, spaces, and common punctuation are allowed).');
      return;
    }

    const existingWords = neverTranslateWords[langKey] || [];
    const key = normalizeTerm(trimmed);
    const duplicate = existingWords.some(word => word !== editingWord && normalizeTerm(word) === key);
    if (duplicate) {
      message.warning('That word or phrase already exists.');
      return;
    }

    const updatedWords = existingWords.map(word => (word === editingWord ? trimmed : word));
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords(prev => ({
        ...prev,
        [langKey]: updatedWords
      }));
      setEditingWord(null);
      message.success('Never translate rule updated successfully');
    }
  };

  const handleRemoveWord = async (word: string) => {
    const updatedWords = (neverTranslateWords[langKey] || []).filter(w => w !== word);
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords(prev => ({
        ...prev,
        [langKey]: updatedWords
      }));
      message.success('Never translate rule removed successfully');
    }
  };

  const handleEditWord = (oldWord: string) => {
    setEditingWord(oldWord);
    setEditedWord(oldWord);
  };

  const handleClose = () => {
    setNewWord('');
    setEditingWord(null);
    setEditedWord('');
    onClose();
  };

  const currentTerms = neverTranslateWords[langKey] || [];

  const handleCopyTerms = async (format: 'readable' | 'pipe') => {
    if (!currentTerms.length) {
      message.info('No rules to copy yet.');
      return;
    }

    const text =
      format === 'readable'
        ? currentTerms.join('\n')
        : currentTerms.map(term => term.trim()).join(' | ');

    try {
      await navigator.clipboard.writeText(text);
      message.success(format === 'readable' ? 'Copied list to clipboard.' : 'Copied import string to clipboard.');
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  return (
    <Modal
      className="account-translation-rules-modal"
      title={
        <span className="account-translation-rules-title">
          Global Never Translate Rules - {currentLanguage}
          <Tooltip title="These words/phrases will never be translated in any of your books when translating to this language">
            <QuestionCircleOutlined className="account-translation-rules-help" />
          </Tooltip>
        </span>
      }
      visible={isVisible}
      onCancel={handleClose}
      footer={
        <div className="account-translation-rules-footer">
          <div className="account-translation-rules-footer-summary" aria-live="polite">
            <strong>{currentTerms.length} never translate rule{currentTerms.length === 1 ? '' : 's'}</strong>
            <span className="account-translation-rules-help-text">
              {editingWord ? 'Finish or cancel the active edit before closing.' : `Applies globally to ${currentLanguage}.`}
            </span>
          </div>
          <Button key="close" onClick={handleClose}>
            Close
          </Button>
        </div>
      }
      width={700}
    >
      <div className="account-translation-rules-body">
      <Spin spinning={isLoading}>
        <div className="account-translation-rules-block">
          <div className="account-translation-rules-field">
            <label className="account-translation-rules-label">
              <strong>Word or Phrase to Never Translate:</strong>
            </label>
            <Input
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Enter word or phrase to never translate"
              style={{ width: '100%' }}
            />
            <div className="account-translation-rules-help-text">
              Separate multiple entries with <code>|</code> (example: <em>Hero | Sidekick | Location</em>).
            </div>
          </div>
          
          <Button 
            type="primary" 
            onClick={handleAddWord} 
            icon={<PlusOutlined />}
          >
            Add Never Translate Rule
          </Button>
        </div>
        
        <div className="account-translation-rules-block is-info">
          <div className="account-translation-rules-toolbar">
            <div>
              <strong>Current Global Never Translate Rules:</strong>
              <Tooltip title="These words/phrases will remain unchanged in all translations to this language">
                <QuestionCircleOutlined className="account-translation-rules-help" />
              </Tooltip>
            </div>
            <Space size="small" wrap>
              <Tooltip title="Copy as a readable list (one per line)">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyTerms('readable')}
                  disabled={!currentTerms.length}
                >
                  Copy List
                </Button>
              </Tooltip>
              <Tooltip title="Copy in bulk format (term1 | term2 | term3)">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyTerms('pipe')}
                  disabled={!currentTerms.length}
                >
                  Copy Import String
                </Button>
              </Tooltip>
            </Space>
          </div>
          
          <div className="account-translation-rules-list">
            {currentLanguage && currentTerms.length > 0 ? (
              <div className="account-translation-rules-grid">
                {currentTerms.map((word, index) => (
                  <div key={index} className={`account-translation-rules-cell ${editingWord === word ? 'is-editing' : ''}`}>
                    <Tag 
                      className="account-translation-rules-tag"
                    >
                      {editingWord === word ? (
                        <>
                          <Input
                            value={editedWord}
                            onChange={(e) => setEditedWord(e.target.value)}
                            style={{ width: '150px', marginRight: '8px' }}
                          />
                          <Tooltip title="Save">
                            <SaveOutlined
                              onClick={handleSaveEdit}
                              className="account-translation-rules-action-icon"
                            />
                          </Tooltip>
                          <span className="account-translation-rules-edit-status">Editing</span>
                        </>
                      ) : (
                        <>
                          <span className="account-translation-rules-text">{word}</span>
                          <div className="account-translation-rules-actions">
                            <Tooltip title="Edit">
                              <EditOutlined
                                onClick={() => handleEditWord(word)}
                                className="account-translation-rules-action-icon"
                              />
                            </Tooltip>
                            <Tooltip title="Delete">
                              <DeleteOutlined
                                onClick={() => handleRemoveWord(word)}
                                className="account-translation-rules-action-icon is-danger"
                              />
                            </Tooltip>
                          </div>
                        </>
                      )}
                    </Tag>
                  </div>
                ))}
              </div>
            ) : (
              <div className="account-translation-rules-empty">
                No global never translate rules set for {currentLanguage}. Add your first rule above.
              </div>
            )}
          </div>
        </div>
      </Spin>
      </div>
    </Modal>
  );
};

export default AccountNeverTranslateModal;
